/**
 * @fileoverview MetadataEditModal component for editing metadata items.
 * Provides a modal interface for editing metadata properties including name, description,
 * linking to existing AIIO elements, and selecting parent items.
 *
 * This component has been refactored into smaller subcomponents for better separation of concerns:
 * - LinkToExistingSection: Handles linking to existing AIIO items
 * - NameField: Manages the name input field
 * - DescriptionField: Manages the description textarea
 * - ParentDropdown: Handles parent item selection (resource groups, orgcharts, etc.)
 * - ModalHeader: Displays the modal title
 * - ModalFooter: Provides save/cancel actions
 * - useMetadataForm: Custom hook for form state management
 * - useModalEventHandlers: Custom hook for keyboard and click event handling
 */

import DescriptionField from "./components/DescriptionField";
import ExtractedNameField from "./components/ExtractedNameField";
import LinkToExistingSection from "./components/LinkToExistingSection";
import ModalFooter from "./components/ModalFooter";
import ModalHeader from "./components/ModalHeader";
import NameField from "./components/NameField";
import ParentDropdown from "./components/ParentDropdown";
import { useMetadataForm } from "./hooks/useMetadataForm";
import { useModalEventHandlers } from "./hooks/useModalEventHandlers";
import type { MetadataEditModalProps } from "./types";

/**
 * MetadataEditModal provides a comprehensive interface for editing metadata items.
 *
 * Features:
 * - Link to existing AIIO elements (departments, roles, resources, etc.)
 * - Edit name and description
 * - Select parent items (resource groups, orgcharts, parent requirements)
 * - Keyboard navigation (Escape to close)
 * - Click outside to close dropdowns
 *
 * @param props - Component props
 * @returns Modal UI or null if not open
 */
export default function MetadataEditModal({
  isOpen,
  onClose,
  categoryTitle,
  categoryKey,
  itemIndex,
  item,
}: MetadataEditModalProps) {
  // Use custom hook for form state management
  const {
    isNewItem,
    germanCategoryTitle,
    localName,
    setLocalName,
    localDescription,
    setLocalDescription,
    linkedId,
    parentId,
    setParentId,
    showDropdown,
    setShowDropdown,
    showParentDropdown,
    setShowParentDropdown,
    availableItems,
    parentItems,
    nameField,
    parentLabel,
    extractedName,
    handleSave,
    handleLinkToExisting,
    handleUnlink,
    handleCancel,
  } = useMetadataForm({
    categoryKey,
    categoryTitle,
    item,
    itemIndex,
    isOpen,
    onClose,
  });

  // Use custom hook for event handling (escape key, click outside)
  useModalEventHandlers({
    isOpen,
    showDropdown,
    setShowDropdown,
    showParentDropdown,
    setShowParentDropdown,
    onCancel: handleCancel,
  });

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-[#f4f4f5] rounded-lg shadow-[0px_14px_28.8px_0px_rgba(0,0,0,0.24),0px_0px_8px_0px_rgba(0,0,0,0.2)] w-[544px] max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader title={germanCategoryTitle} />

        {/* Content */}
        <div className="px-6 py-8 overflow-y-auto flex-1 space-y-8">
          <LinkToExistingSection
            isVisible={!isNewItem && availableItems.length > 0}
            categoryLabel={germanCategoryTitle}
            availableItems={availableItems}
            nameField={nameField}
            linkedId={linkedId}
            currentName={localName}
            isDropdownOpen={showDropdown}
            onToggleDropdown={() => setShowDropdown(!showDropdown)}
            onLinkItem={handleLinkToExisting}
            onUnlink={handleUnlink}
          />
          <ParentDropdown
            parentItems={parentItems}
            selectedParentId={parentId}
            label={parentLabel}
            isOpen={showParentDropdown}
            disabled={!!linkedId}
            onToggle={() => setShowParentDropdown(!showParentDropdown)}
            onSelect={(id) => {
              setParentId(id);
              setShowParentDropdown(false);
            }}
          />
          <NameField
            value={localName}
            onChange={setLocalName}
            disabled={!!linkedId}
            autoFocus={!linkedId}
          />

          {linkedId && extractedName && (
            <ExtractedNameField value={extractedName} />
          )}

          <DescriptionField
            value={localDescription}
            onChange={setLocalDescription}
            disabled={!!linkedId}
          />
        </div>

        <ModalFooter onCancel={handleCancel} onSave={handleSave} />
      </div>
    </div>
  );
}
